import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge } from "react-bootstrap";
import moment from "moment-timezone";
import { DataTable, Icon, Modal } from "../widgets";
import {
  toDisplayDateTimeString,
  toDisplayTimeString,
  secondsToTime,
} from "../../helpers/moment-ex";
import { Unit, formatNumber } from "../../helpers/unit";
import { isDeveloper } from "../../helpers/common";
import {
  setBulkLoad,
  removeBulkLoad,
  deleteLoad,
  setDeletedLoad,
  editLoad,
  editBulkLoad,
} from "../../actions/equipment-group";
import LoadForm from "./LoadForm";
import { LoadNotePopup } from "./LoadNotePopup";
import { SubNav } from "../layout/submenu";
import { useLoadHistoryContext } from "./context/LoadHistoryContext";
import {
  DISPLAY_DATETIMESECONDS_FORMAT2,
  DISPLAY_DATE_WITH_MIN_SEC_MSEC,
  LOAD_TYPE,
} from "../../constants";
import LoadPreview from "./LoadPreview";
import LoadLocationPopUp from "./LoadLocationPopUp";
import LoadSample from "../../constants/LoadSample";
import LoadSampleProfileTable from "../../constants/LoadSampleProfileTable";

const LoadRow = (props) => {
  const dispatch = useDispatch();
  const newLoadIds = useSelector(
    (state) => state.equipmentGroup.loadHistory.newLoadIds
  );

  const {
    isAllowEdit,
    isAllowDelete,
    farmId,
    loadActivityId,
    load,
    serialNumber,
    showableCustomColumns,
    isAllowCreate,
    handleLoadRestore,
    initialHeaders,
  } = props;
  const [showModal, setShowModal] = useState({
    edit: false,
    delete: false,
    location: false,
    loadSample: false,
    loadSampleProfileTable: false,
  });
  // const [isSampleSaving, setIsSampleSaving] = useState(false);
  const selectedPlanType = useSelector((state) => state.plan.selectedPlanType);
  const farmSettings = useSelector(
    (state) => state.farm.farmSettings.farm_config
  );

  const [planTypeId, setPlanTypeId] = useState([]);

  useEffect(() => {
    if (
      farmSettings &&
      farmSettings.seed_load_sample &&
      farmSettings.seed_load_sample.plan_type_ids
    )
      return setPlanTypeId(farmSettings.seed_load_sample.plan_type_ids);
  }, [farmSettings, selectedPlanType]);

  const [showPreview, setShowPreview] = useState(false);
  const {
    bulkEditLoadList,
    handleBulkEditLoadSelect,
    enableBulkEdit,
    showDeletedLoads,
  } = useLoadHistoryContext();

  const isClonedNewLoad = useMemo(() => {
    return loadActivityId && newLoadIds && newLoadIds.length > 0
      ? newLoadIds.indexOf(loadActivityId) >= 0
      : false;
  }, [newLoadIds]);

  const unit = Unit.getInstance();
  const isFormatToSmallScale = false;

  const handleDeleteLoad = () => {
    dispatch(
      deleteLoad({ load_activity_uuid: loadActivityId }, async (isDeleted) => {
        if (isDeleted) {
          dispatch(setDeletedLoad(loadActivityId));
          dispatch(removeBulkLoad(loadActivityId));
        }
      })
    );
  };

  const handleShowModal = (type, show) =>
    setShowModal({ ...showModal, [type]: show });

  const isActive = showModal.edit || showModal.delete || showModal.location;
  const isWebfmLoad =
    load.hasOwnProperty("meta_data") &&
      load.meta_data.hasOwnProperty("location") &&
      !load.meta_data.location.latitude
      ? true
      : false;
  const isClonedLoad = load.parent_load_activity_uuid ? true : false;
  const isRestored = load.restored_date ? true : false;
  const isSelected = bulkEditLoadList
    .map((item) => item.load_activity_uuid)
    .includes(load.load_activity_uuid);
  const selectedLoad =
    bulkEditLoadList.find(
      (t) => t.load_activity_uuid === load.load_activity_uuid
    ) || {};

  const loadCapacity = useMemo(() => {
    const selectedLoad =
      bulkEditLoadList.find(
        (t) => t.load_activity_uuid === load.load_activity_uuid
      ) || {};

    const formatLoadCapacity = (loadCapacity) =>
      isFormatToSmallScale
        ? unit.formatNumberToSmallScale(loadCapacity)
        : formatNumber(unit.toFixedOf(loadCapacity));

    if (selectedLoad.hasOwnProperty("load_capacity")) {
      return formatLoadCapacity(selectedLoad.load_capacity, false);
    }
    return formatLoadCapacity(load.load_capacity);
  }, [load, bulkEditLoadList]);

  const hidePreview = () => {
    setShowPreview(false);
  };

  const loadGrossWeight = useMemo(() => {
    const selectedLoad =
      bulkEditLoadList.find(
        (t) => t.load_activity_uuid === load.load_activity_uuid
      ) || {};

    // {load.load_gross_weight ? formatNumber(load.load_gross_weight) : load.estimated_load_capacity}

    const formatLoadCapacity = (loadGrossWeight) =>
      isFormatToSmallScale
        ? unit.formatNumberToSmallScale(loadGrossWeight)
        : formatNumber(unit.toFixedOf(loadGrossWeight));

    if (selectedLoad && selectedLoad.load_gross_weight)
      return formatLoadCapacity(selectedLoad.load_gross_weight, false);
    else if (selectedLoad && selectedLoad.estimated_load_capacity)
      return formatLoadCapacity(selectedLoad.estimated_load_capacity, false);
    else if (load && load.load_gross_weight)
      return formatLoadCapacity(load.load_gross_weight, false);
    else return formatLoadCapacity(load.estimated_load_capacity);
  }, [load, bulkEditLoadList]);

  const loadTareWeight = useMemo(() => {
    const selectedLoad =
      bulkEditLoadList.find(
        (t) => t.load_activity_uuid === load.load_activity_uuid
      ) || {};

    const formatLoadCapacity = (loadTareWeight) =>
      isFormatToSmallScale
        ? unit.formatNumberToSmallScale(loadTareWeight)
        : formatNumber(unit.toFixedOf(loadTareWeight));

    if (selectedLoad.hasOwnProperty("tare_weight")) {
      return formatLoadCapacity(selectedLoad.tare_weight, false);
    }
    return formatLoadCapacity(load.tare_weight);
  }, [load, bulkEditLoadList]);

  const loadLightWeight = useMemo(() => {
    const selectedLoad =
      bulkEditLoadList.find(
        (t) => t.load_activity_uuid === load.load_activity_uuid
      ) || {};

    const formatLoadCapacity = (loadLightWeight) =>
      isFormatToSmallScale
        ? unit.formatNumberToSmallScale(loadLightWeight)
        : formatNumber(unit.toFixedOf(loadLightWeight));

    if (selectedLoad.hasOwnProperty("light_weight")) {
      return formatLoadCapacity(selectedLoad.light_weight, false);
    }
    return formatLoadCapacity(load.light_weight);
  }, [load, bulkEditLoadList]);

  const getEventStatus = (statusId) => {
    const status = {
      1: "Start",
      2: "Pause",
      3: "Resume",
      4: "Stop",
      5: "Cancel",
      6: "Approved",
      7: "Cancel Approved",
    };
    return status.hasOwnProperty(statusId) ? status[statusId] : statusId;
  };

  const handleRatePopUpClick = (e) => {
    const allItems = document.querySelectorAll(".rate_columns");
    allItems.forEach((item) => item.classList.remove("active"));
    e.currentTarget.classList.add("active");
  };

  const handleRatePopUpClose = (e) => {
    e.preventDefault();
    const allItems = document.querySelectorAll(".rate_columns");
    setTimeout(() => {
      allItems.forEach((item) => item.classList.remove("active"));
    },100)
  };
  

  const getCellValue = (keyName) => {
    const getSelectedLoad =
      selectedLoad && selectedLoad.hasOwnProperty(keyName)
        ? selectedLoad[keyName]
        : null;
    const getLoadValue =
      getSelectedLoad ||
      (load && load.hasOwnProperty(keyName) ? load[keyName] : null);
    const { division, area, unit } =
      load && load.field_geo_shape_properties
        ? load.field_geo_shape_properties
        : {};
    const cellObj = {
      load_gross_weight: loadGrossWeight,
      tare_weight: loadTareWeight,
      light_weight: loadLightWeight,
      load_capacity: loadCapacity,
      load_date: getLoadValue
        ? toDisplayDateTimeString(load.load_date, false, true)
        : null,
      load_seconds: getLoadValue ? (
        isDeveloper() ? (
          <>
            <span>
              {moment
                .utc(load.start_date)
                .format(DISPLAY_DATETIMESECONDS_FORMAT2)}{" "}
              -{" "}
              {moment
                .utc(load.end_date)
                .format(DISPLAY_DATETIMESECONDS_FORMAT2)}{" "}
              @{" "}
              {moment
                .utc(load.created_date)
                .format(DISPLAY_DATE_WITH_MIN_SEC_MSEC)}{" "}
            </span>{" "}
            ({toDisplayTimeString(secondsToTime(load.load_seconds, true))})
          </>
        ) : (
          toDisplayTimeString(secondsToTime(load.load_seconds, true))
        )
      ) : null,
      crew_name:
        load.load_type && load.load_type === LOAD_TYPE.AUTOMATIC
          ? getLoadValue
          : getLoadValue && getLoadValue !== "Unknown"
            ? `${getLoadValue}${" (m)"}`
            : getLoadValue,
      division: division ? division : "Unknown",
      area: area ? area : "Unknown",
      unit: unit ? unit : "Unknown",
    };
    return cellObj.hasOwnProperty(keyName) ? cellObj[keyName] : getLoadValue;
  };

  const updatedColumns = showableCustomColumns.map((column, columnIndex) => {
    const header = initialHeaders.find(
      (col) => col.key === column && col.isShow
    );
    if (!header) {
      return null; // Handle missing columns
    }
    // const { division, area, unit } = load && load.field_geo_shape_properties ? load.field_geo_shape_properties : {};
    let cellValue = getCellValue(column);
    cellValue =
      Array.isArray(cellValue) && column !== "events"
        ? cellValue.length > 0
          ? cellValue.toString()
          : "Unknown"
        : cellValue;

    switch (column) {
      case "id":
        return (
          <DataTable.Cell
            key={columnIndex}
            id={loadActivityId}
            style={{ textAlign: `${enableBulkEdit ? "center" : ""}` }}
          >
            {/* (load.transfer_type === 1) && */ serialNumber}
          </DataTable.Cell>
        );
      case "approved":
        return load.approved ? (
          <DataTable.Cell key={columnIndex}>
            <Badge
              title="Approved"
              variant="success"
              className="badge-sm mr-2"
              pill
            >
              <Icon className="fa fa-check" />
            </Badge>{" "}
            {toDisplayDateTimeString(load.approved_date_tz, false, false)}
          </DataTable.Cell>
        ) : (
          <DataTable.Cell key={columnIndex}>
            <Badge
              title="Not Approved"
              variant="danger"
              className="badge-sm"
              pill
            >
              <Icon className="fa fa-times" />
            </Badge>
          </DataTable.Cell>
        );
      case "action":
        return !enableBulkEdit &&
          !showDeletedLoads &&
          (isAllowEdit || isAllowDelete || isAllowCreate) ? (
          <React.Fragment key={columnIndex}>
            <DataTable.Cell>
              <div className="d-flex justify-content-between align-items-center">
                {isAllowEdit && (
                  <Icon
                    title={"Edit"}
                    onClick={() => handleShowModal("edit", true)}
                    className="fa fa-pencil cursor-pointer"
                    ariaHidden="true"
                  />
                )}
                {isAllowDelete && !isClonedNewLoad && (
                  <Icon
                    title={"Delete"}
                    onClick={() => handleShowModal("delete", true)}
                    className="fa fa-trash ml-2 cursor-pointer"
                    ariaHidden="true"
                  />
                )}
                {isAllowEdit || isAllowDelete ? (
                  <span className="vl mx-2"></span>
                ) : null}
                {!enableBulkEdit && !showDeletedLoads && isAllowCreate && (
                  <Icon
                    title="Copy"
                    className="fa fa-clone cursor-pointer"
                    ariaHidden="true"
                    onClick={() => dispatch(setBulkLoad(loadActivityId))}
                  />
                )}
                {isClonedNewLoad && (
                  <Icon
                    title="Remove"
                    className="fa fa-times cursor-pointer mx-1"
                    ariaHidden="true"
                    onClick={() => dispatch(removeBulkLoad(loadActivityId))}
                  />
                )}
                {!enableBulkEdit && !showDeletedLoads && (
                  <span className="mx-1">
                    <LoadNotePopup load={load} />
                  </span>
                )}

                {farmSettings &&
                  farmSettings.seed_piece_profile_sample &&
                  farmSettings.seed_piece_profile_sample.visible && (
                    <div>
                      {" "}
                      <Icon
                        title="Load Sample Profile Table"
                        onClick={() =>
                          handleShowModal("loadSampleProfileTable", true)
                        }
                        className="fa fa-wpforms mx-1 cursor-pointer"
                        ariaHidden="true"
                      />
                    </div>
                  )}

                {farmSettings &&
                  farmSettings.seed_load_sample &&
                  farmSettings.seed_load_sample.visible &&
                  planTypeId.includes(selectedPlanType) && (
                    <div>
                      {" "}
                      <Icon
                        title="Load Sample"
                        onClick={() => handleShowModal("loadSample", true)}
                        className="fa fa-flask mx-1 cursor-pointer"
                        ariaHidden="true"
                      />
                    </div>
                  )}

                {!enableBulkEdit && !showDeletedLoads && (
                  <span className="mx-1">
                    {
                      <Icon
                        className="fa fa-print cursor-pointer"
                        onClick={() => setShowPreview(true)}
                      />
                    }
                  </span>
                )}

                <Icon
                  title={
                    load.events && load.events.length !== 0
                      ? "Location"
                      : "Location not found"
                  }
                  onClick={() =>
                    handleShowModal(
                      "location",
                      load.events && load.events.length !== 0
                    )
                  }
                  className="fa fa-map-marker cursor-pointer mx-1"
                  ariaHidden="true"
                />
              </div>
            </DataTable.Cell>
            <Modal.Confirm
              className="modal-bordered"
              show={showModal.delete}
              onConfirm={handleDeleteLoad}
              onHide={() => handleShowModal("delete", false)}
              iconClassName="fa fa-question"
              title={{ content: "Confirmation" }}
              content={`Do you want to delete?`}
            />
          </React.Fragment>
        ) : (
          <DataTable.Cell key={columnIndex}></DataTable.Cell>
        );
      case "restore":
        return showDeletedLoads ? (
          <DataTable.Cell
            style={{ width: "85px", textAlign: "center" }}
            key={columnIndex}
          >
            <Icon
              className="fa fa-undo cursor-pointer"
              onClick={() => handleLoadRestore(loadActivityId)}
            />
          </DataTable.Cell>
        ) : (
          <DataTable.Cell key={columnIndex}></DataTable.Cell>
        );
      case "field_geo_shape_name":
        return (
          <DataTable.Cell key={columnIndex} className={`row_field_name`}>
            <div className="d-flex align-items-center">
              <span title="Field"> {cellValue}</span>

              {/* {division
                                ? <> <span className="vl mx-2"></span>
                                    <span title="Division Code">{division}</span></> : null}
                            {area ? <><span className="vl mx-2"></span>
                                <span title="Area Code">{area}</span></> : null}
                            {unit ? <><span className="vl mx-2"></span>
                                <span title="Unit Code">{unit}</span></> : null} */}
            </div>
          </DataTable.Cell>
        );
      case "events":
        return (
          <DataTable.Cell key={columnIndex}>
            {Array.isArray(cellValue) && cellValue.length > 0
              ? cellValue
                .sort(
                  (a, b) =>
                    new Date(a.event_date).valueOf() -
                    new Date(b.event_date).valueOf()
                )
                .map((e, index) => {
                  return (
                    <div
                      key={`sequence_${index}`}
                      className={`planter_allocations_container ${!getCellValue("load_seconds") ? "text-danger" : ""
                        }`}
                    >
                      {e && (
                        <>
                          <span>
                            {e.event_status
                              ? getEventStatus(e.event_status)
                              : "No status"}
                          </span>
                          <span>
                            {e.event_date_tz
                              ? ` @ ${moment
                                .utc(e.event_date_tz)
                                .format(DISPLAY_DATETIMESECONDS_FORMAT2)}`
                              : "-"}
                          </span>
                          <span>
                            {e.event_id ? ` - ${e.event_id}` : ""}
                            {e.parent_event_id
                              ? ` - ${e.parent_event_id}`
                              : ""}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })
              : "-"}
          </DataTable.Cell>
        );
      case "rate_info":
        return (
          <DataTable.Cell key={columnIndex} style={{ minWidth: "100px", whiteSpace: 'nowrap' }}>
            {(cellValue && cellValue.rate_value) ? <div>
              <div className="rate_columns rate_samples" onClick={(e) => handleRatePopUpClick(e)}>
                <div className="rate_value mr-1">
                  <i className="fa fa-usd"></i>
                  {cellValue && (cellValue.rate_value !== cellValue.total) ? cellValue.total : cellValue.rate_value}
                </div>
                <i className="fa fa-info-circle" aria-hidden="true"></i>

                <div className="arrow_popover">
                    <div className="popover">
                      <div className="rate_header header_none">
                        <span>Rate Details</span>
                        <div onClick={(e) => handleRatePopUpClose(e)}>
                          <i className="fa fa-times"></i>
                        </div>
                      </div>
                      <div className="arrow left_center"></div>
                      <div className="my-2">
                        <div className="rate_lists">
                          Rate{' '}
                          <span className="rate_list_value"><i className="fa fa-usd"></i>{cellValue && cellValue.rate_value ? cellValue.rate_value : 0}{' '}</span>
                          <strong>
                            {cellValue.rate_type_name ? `(${cellValue.rate_type_name})` : null}
                          </strong>
                        </div> 
                        {cellValue && cellValue.surcharge_value ? <div className="rate_lists">
                          Custom Surcharge{' '}
                          <span className="rate_list_value"><i className="fa fa-usd"></i>{cellValue && cellValue.surcharge_value ? cellValue.surcharge_value : 0}{' '}</span>
                          <strong>
                            {cellValue.surcharge_type_name ? `(${cellValue.surcharge_type_name})` : null}
                          </strong>
                        </div> : null}
                        {cellValue && cellValue.own_bed_rate_value ? <div className="rate_lists">
                          Own Bed Rate{' '}
                          <span className="rate_list_value"><i className="fa fa-usd"></i>{cellValue && cellValue.own_bed_rate_value ? cellValue.own_bed_rate_value : 0}{' '}</span>
                        </div> : null}
                        {cellValue && cellValue.additional_surcharge_percent ? <div className="rate_lists">
                          Additonal Surcharge{' '}
                          <span className="rate_list_value">{cellValue && cellValue.additional_surcharge_percent ? cellValue.additional_surcharge_percent : 0}</span>
                          <strong>
                            %
                          </strong>
                        </div> : null}
                        <div className="rate_lists">
                          Total{' '}
                          <span className="rate_list_value"><i className="fa fa-usd"></i>{cellValue.total ? `${unit.toFixedOf(cellValue.total, 2)}` : null}</span>
                        </div>
                      </div>
                    </div>
                  </div>
              </div>
              {/* <span className="d-block"><strong>Rate:</strong> {cellValue && cellValue.rate_value ? `${cellValue.rate_value} (${cellValue.rate_type_name})` : "unKnown"} </span>
              {cellValue.surcharge_value ? <span className="d-block"><strong>Surcharge:</strong> {cellValue && cellValue.surcharge_value ? `${cellValue.surcharge_value} (${cellValue.surcharge_type_name})` : 0} </span> : null}
              <span className="d-block"><strong>Total:</strong> {cellValue && cellValue.total ?  unit.toFixedOf(cellValue.total, 2) : "unKnown"}</span> */}
            </div> : <div className="text-danger">Unknown</div>}
          </DataTable.Cell>
        );
      default:
        return (
          <DataTable.Cell
            key={columnIndex}
            style={{ color: `${cellValue === "Unknown" ? "red" : ""}` }}
          >
            {cellValue}
          </DataTable.Cell>
        );
    }
  });

  const saveLoadSample = (saveLoad) => {
    dispatch(
      editLoad(saveLoad, (isSaved, params) => {
        if (isSaved) {
          // After saving the sample we are updating the Load History Reducer.
          dispatch(editBulkLoad(load.load_activity_uuid, params));
        }
      })
    );
  };

  return (
    <>
      <LoadForm
        farmId={farmId}
        show={showModal.edit}
        onHide={() => handleShowModal("edit", false)}
        isCloned={isClonedLoad}
        load={load}
      ></LoadForm>
      <LoadLocationPopUp
        farmId={farmId}
        show={showModal.location}
        onHide={() => handleShowModal("location", false)}
        isCloned={isClonedLoad}
        load={load}
      ></LoadLocationPopUp>
      <DataTable.Row
        style={{
          backgroundColor: isWebfmLoad && !isClonedLoad ? "#e2ffe9" : null,
        }}
        className={`${load.transfer_type === 2 &&
          !isClonedLoad &&
          "cart-type"} ${isClonedLoad ? "is-cloned" : ""} ${isActive &&
          "is-active"} ${isSelected ? "is-selected" : ""} ${isRestored ? "is-restored" : ""
          }`}
      >
        {enableBulkEdit && (
          <DataTable.Cell>
            <SubNav.Checkbox
              className="square"
              checked={isSelected}
              onChange={(e) =>
                handleBulkEditLoadSelect([load], e.target.checked)
              }
            />
          </DataTable.Cell>
        )}
        {updatedColumns}
      </DataTable.Row>
      <LoadPreview show={showPreview} load={load} onHide={hidePreview} />
      {showModal.loadSample && (
        <LoadSample
          show={showModal.loadSample}
          onHide={() => setShowModal({ ...showModal, loadSample: false })}
          load={load}
          farmId={farmId}
          saveSample={saveLoadSample}
        />
      )}
      {showModal.loadSampleProfileTable && (
        <LoadSampleProfileTable
          show={showModal.loadSampleProfileTable}
          onHide={() =>
            setShowModal({ ...showModal, loadSampleProfileTable: false })
          }
          load={load}
          farmId={farmId}
          saveSample={saveLoadSample}
        />
      )}
    </>
  );
};
export default LoadRow;
